// A13: per-workspace provider-key overrides. Same shape as the
// org-level ProviderKeysPanel but keyed on workspace_id. The backend
// looks workspace-scoped first, falls back to org-scoped, so an
// empty list here just means "use the org's keys".

import { useEffect, useState } from "react";
import {
  ApiError,
  listWorkspaceProviderKeys,
  revokeWorkspaceProviderKey,
  setWorkspaceProviderKey,
  type ProviderKeySummary,
} from "../api/client";

interface Props {
  workspaceId: number;
}

// Sandbox runtime is Waytide-managed (no BYOA path), so the runtime
// provider isn't on this panel.
const SUPPORTED_PROVIDERS: Array<{ id: string; label: string; hint: string }> = [
  {
    id: "openrouter",
    label: "OpenRouter",
    hint: "Default model gateway. Override per-workspace when this workspace's spend should bill to a different OpenRouter account.",
  },
  {
    id: "resend",
    label: "Resend (custom email domain)",
    hint: "BYOK to send Captain emails from your own domain instead of @waytide.dev.",
  },
  {
    id: "anthropic",
    label: "Anthropic (direct)",
    hint: "anthropic/<model> routes through this key (skipping OpenRouter).",
  },
  {
    id: "openai",
    label: "OpenAI (direct)",
    hint: "openai/<model> routes through this key.",
  },
  {
    id: "google",
    label: "Google Gemini (direct)",
    hint: "google/<model> routes through this key.",
  },
];

export function WorkspaceKeysPanel({ workspaceId }: Props) {
  const [keys, setKeys] = useState<ProviderKeySummary[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const resp = await listWorkspaceProviderKeys(workspaceId);
      setKeys(resp.keys);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const save = async (provider: string) => {
    const value = (draft[provider] ?? "").trim();
    if (!value) return;
    setBusy(provider);
    setError(null);
    try {
      await setWorkspaceProviderKey(workspaceId, provider, value);
      setDraft((prev) => ({ ...prev, [provider]: "" }));
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (provider: string) => {
    setBusy(`revoke:${provider}`);
    setError(null);
    try {
      await revokeWorkspaceProviderKey(workspaceId, provider);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const byProvider = new Map(keys.map((k) => [k.provider, k]));

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="workspace-keys-panel"
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">
          Workspace-scoped provider keys
        </h3>
        <p className="mt-1 text-xs text-muted">
          Optional per-workspace overrides. Empty here = falls back to
          your org's keys. Captains running multiple workspaces under
          separate provider accounts (one OpenRouter for Lureo, another
          for Imvizar) override here.
        </p>
      </header>
      <div className="flex flex-col gap-2">
        {SUPPORTED_PROVIDERS.map((p) => {
          const existing = byProvider.get(p.id);
          return (
            <div
              key={p.id}
              className="flex flex-col gap-1 rounded-md border border-edge bg-surface-raised p-2"
              data-testid={`workspace-key-${p.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-primary">
                  {p.label}
                </span>
                {existing ? (
                  <span className="font-code text-[0.625rem] text-faint">
                    ••••{existing.last4}
                  </span>
                ) : (
                  <span className="text-[0.625rem] text-faint">
                    (using org key)
                  </span>
                )}
              </div>
              <p className="text-[0.625rem] text-muted">{p.hint}</p>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={draft[p.id] ?? ""}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [p.id]: e.target.value }))
                  }
                  placeholder={existing ? "Replace key…" : "Paste key…"}
                  className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 font-code text-xs text-primary placeholder:text-faint"
                />
                <button
                  type="button"
                  onClick={() => void save(p.id)}
                  disabled={busy !== null || !(draft[p.id] ?? "").trim()}
                  className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
                >
                  {busy === p.id ? "Saving…" : "Save"}
                </button>
                {existing && (
                  <button
                    type="button"
                    onClick={() => void revoke(p.id)}
                    disabled={busy !== null}
                    className="rounded-md border border-edge px-2 py-1 text-[0.625rem] text-muted hover:border-danger hover:text-danger disabled:opacity-50"
                  >
                    {busy === `revoke:${p.id}` ? "…" : "Revoke"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </section>
  );
}
